/**
 * SignaturePad — touch & mouse-enabled signature canvas.
 * Returns signature as base64 PNG (draw mode) or typed string (type mode).
 */
import { useRef, useState, useEffect, useCallback } from "react";
import { RotateCcw, PenLine, Keyboard } from "lucide-react";

export default function SignaturePad({ onChange, value, disabled, label = "Signature" }) {
    const canvasRef = useRef(null);
    const [mode, setMode] = useState("draw");
    const [typedName, setTypedName] = useState(value && !value.startsWith("data:image/") ? value : "");
    const [isEmpty, setIsEmpty] = useState(true);
    const drawing = useRef(false);
    const lastPos = useRef(null);
    const hydrated = useRef(false);

    const paintDataUrl = useCallback((dataUrl) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const img = new Image();
        img.onload = () => {
            const ctx = canvas.getContext("2d");
            const rect = canvas.getBoundingClientRect();
            ctx.drawImage(img, 0, 0, rect.width, rect.height);
            setIsEmpty(false);
        };
        img.src = dataUrl;
    }, []);

    const initCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * window.devicePixelRatio;
        canvas.height = rect.height * window.devicePixelRatio;
        const ctx = canvas.getContext("2d");
        ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
        ctx.strokeStyle = "#1F1F1F";
        ctx.lineWidth = 2.4;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
    }, []);

    // Hydrate the pad from a previously-saved value (typed name or drawn PNG).
    useEffect(() => {
        if (hydrated.current || !value) return;
        hydrated.current = true;
        if (value.startsWith("data:image/")) {
            setMode("draw");
        } else {
            setMode("type");
            setTypedName(value);
        }
    }, [value]);

    useEffect(() => {
        if (mode === "draw") {
            initCanvas();
            if (value && value.startsWith("data:image/")) paintDataUrl(value);
        }
    }, [mode, initCanvas, value, paintDataUrl]);

    const getPos = (e, canvas) => {
        const rect = canvas.getBoundingClientRect();
        const touch = e.touches?.[0] || e;
        return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    };

    const startDraw = (e) => {
        if (disabled) return;
        e.preventDefault();
        const canvas = canvasRef.current;
        drawing.current = true;
        lastPos.current = getPos(e, canvas);
    };
    const draw = (e) => {
        if (!drawing.current || disabled) return;
        e.preventDefault();
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        const pos = getPos(e, canvas);
        ctx.beginPath();
        ctx.moveTo(lastPos.current.x, lastPos.current.y);
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
        lastPos.current = pos;
        setIsEmpty(false);
    };
    const endDraw = (e) => {
        if (!drawing.current) return;
        e.preventDefault();
        drawing.current = false;
        const canvas = canvasRef.current;
        if (onChange) onChange(canvas.toDataURL("image/png"));
    };
    const clearCanvas = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width / window.devicePixelRatio, canvas.height / window.devicePixelRatio);
        setIsEmpty(true);
        if (onChange) onChange("");
    };
    const handleTypeChange = (v) => {
        setTypedName(v);
        if (onChange) onChange(v);
    };

    return (
        <div className="space-y-2" data-testid="signature-pad">
            <div className="flex items-center justify-between">
                <p className="text-[13px] font-semibold text-[#1F1F1F]">{label}</p>
                <div className="flex gap-1">
                    <button type="button" onClick={() => setMode("draw")}
                        data-testid="sig-mode-draw"
                        className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg border transition-colors ${mode === "draw"
                                ? "bg-[#1F1F1F] text-white border-[#1F1F1F]"
                                : "text-[#6F6A64] border-[#E5E0DA] hover:bg-[#F6F4F0]"
                            }`}>
                        <PenLine size={11} /> Draw
                    </button>
                    <button type="button" onClick={() => setMode("type")}
                        data-testid="sig-mode-type"
                        className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg border transition-colors ${mode === "type"
                                ? "bg-[#1F1F1F] text-white border-[#1F1F1F]"
                                : "text-[#6F6A64] border-[#E5E0DA] hover:bg-[#F6F4F0]"
                            }`}>
                        <Keyboard size={11} /> Type
                    </button>
                </div>
            </div>

            {mode === "draw" ? (
                <div className="relative">
                    <canvas
                        ref={canvasRef}
                        data-testid="signature-canvas"
                        className={`w-full rounded-xl border-2 ${disabled
                                ? "opacity-50 cursor-not-allowed bg-[#F6F4F0]"
                                : "cursor-crosshair bg-white touch-none"
                            } ${isEmpty ? "border-dashed border-[#D8D5CF]" : "border-[#C0BCB4]"}`}
                        style={{ height: 130 }}
                        onMouseDown={startDraw} onMouseMove={draw}
                        onMouseUp={endDraw} onMouseLeave={endDraw}
                        onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
                    />
                    {isEmpty && !disabled && (
                        <p className="absolute inset-0 flex items-center justify-center text-[#C0BCB4] text-sm pointer-events-none italic">
                            Sign here with your finger or mouse
                        </p>
                    )}
                    {!isEmpty && !disabled && (
                        <button type="button" onClick={clearCanvas}
                            data-testid="signature-clear"
                            className="absolute top-2 right-2 p-1 hover:bg-red-50 rounded text-[#9A968F] hover:text-red-600 transition-colors">
                            <RotateCcw size={13} />
                        </button>
                    )}
                </div>
            ) : (
                <input
                    type="text"
                    value={typedName}
                    onChange={(e) => handleTypeChange(e.target.value)}
                    onBlur={(e) => handleTypeChange(e.target.value)}
                    disabled={disabled}
                    placeholder="Type your full name to sign"
                    data-testid="signature-typed-input"
                    className="w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white italic text-[#1F1F1F] focus:outline-none focus:border-[var(--brand-accent,_#C75A1B)] disabled:bg-[#F6F4F0]"
                    style={{ fontFamily: "Georgia, serif", fontSize: "16px" }}
                />
            )}

            <p className="text-[11px] text-[#9A968F]">
                {mode === "draw"
                    ? "Draw your signature above using your finger (mobile) or mouse."
                    : "Type your full legal name as a digital signature."}
            </p>
        </div>
    );
}
